<script>
import { authComputed } from '@state/helpers'

export default {
  name: 'OrganizationSignupForm',
  props: {
    signUpError: {
      type: String,
      required: true,
    },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      nonprofitName: '',
      profilePageName: '',
      missionStatement: '',
      adminName: '',
      adminEmail: '',
      mainWebsite: '',
      volunteerWebsite: '',
      isGoodStanding: 0,
      nonprofitClassification: '',
      isReligious: 0,
      isDiscriminating: 0,
      isPolitical: 0,
      isFederal: 0,
      errors: {
        nonprofitName: '',
        profilePageName: '',
        missionStatement: '',
        adminName: '',
        adminEmail: '',
        mainWebsite: '',
        volunteerWebsite: '',
        nonprofitClassification: '',
      },
      prettySignUpErrors: {
        email_taken: 'Email already exists',
      },
    }
  },
  computed: {
    ...authComputed,
    // Merge client-side and server-side errors, with server errors taking precedence
    displayErrors() {
      const merged = { ...this.errors }
      for (const field in this.serverErrors) {
        if (this.serverErrors[field] && this.serverErrors[field].length > 0) {
          merged[field] = this.serverErrors[field][0]
        }
      }
      return merged
    },
  },
  mounted() {},
  methods: {
    async signupOrganization() {
      if (!this.adminEmail) {
        this.displayErrors.adminEmail = 'Email required.'
      } else if (!this.validEmail(this.adminEmail)) {
        this.displayErrors.adminEmail = 'Valid email required.'
      }
      if (!this.nonprofitName) {
        this.displayErrors.nonprofitName = 'Nonprofit name required.'
      }
      if (!this.profilePageName) {
        this.displayErrors.profilePageName = 'Profile page name required.'
      }
      if (!this.missionStatement) {
        this.displayErrors.missionStatement = 'Mission statement required.'
      }
      if (!this.adminName) {
        this.displayErrors.adminName = 'Admin name required.'
      }
      if (!this.adminEmail) {
        this.displayErrors.adminEmail = 'Admin email required.'
      }
      if (!this.mainWebsite) {
        this.displayErrors.mainWebsite = 'Main website required.'
      } else {
        const isValid = await this.validUrl(this.mainWebsite)
        if (!isValid) {
          this.displayErrors.mainWebsite = 'Please enter a valid, accessible URL'
        }
      }
      if (!this.volunteerWebsite) {
        this.displayErrors.volunteerWebsite = 'Volunteer website required.'
      } else {
        const isValid = await this.validUrl(this.volunteerWebsite)
        if (!isValid) {
          this.displayErrors.volunteerWebsite = 'Please enter a valid, accessible URL'
        }
      }
      if (!Object.values(this.errors).some((x) => x !== '')) {
        this.$emit('clear-sign-up-errors')
        this.$emit('post-signup-organization', {
          nonprofitName: this.nonprofitName,
          profilePageName: this.profilePageName,
          missionStatement: this.missionStatement,
          adminName: this.adminName,
          adminEmail: this.adminEmail,
          mainWebsite: this.mainWebsite,
          volunteerWebsite: this.volunteerWebsite,
          isGoodStanding: this.isGoodStanding,
          nonprofitClassification: this.nonprofitClassification,
          isReligious: this.isReligious,
          isDiscriminating: this.isDiscriminating,
          isPolitical: this.isPolitical,
          isFederal: this.isFederal,
        })
      }
    },
    validEmail: function(email) {
      const rule = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return rule.test(email)
    },
    validUrl: async function(url) {
      try {
        // Add protocol if missing
        let testUrl = url
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          testUrl = 'https://' + url
        }

        // Validate URL format first (will throw if invalid)
        // eslint-disable-next-line no-new
        new URL(testUrl)

        // Try to fetch the URL (using mode: 'no-cors' to bypass CORS, but we can still detect network errors)
        await fetch(testUrl, {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-cache',
        })

        // With no-cors mode, we get opaque response, but if fetch succeeds, the site is reachable
        return true
      } catch (e) {
        // If fetch fails, the URL is not accessible
        return false
      }
    },
    clearSignUpErrors(fieldName) {
      if (fieldName) {
        this.errors[fieldName] = ''
      }
      this.$emit('clear-sign-up-errors')
    },
    toggleShow() {
      this.showPassword = !this.showPassword
    },
  },
}
</script>

<template>
  <div class="flex-col">
    <h1 class="home-headline1">Let's start sharing kyndfulness</h1>
    <div v-if="!successSignupOrganization" class="">
      <h2 class="home-headline2">Organization Sign Up</h2>
      <div class="text-left">
        <fieldset class="mt-8 flex -mx-2">
          <legend class="mb-3 text-gray">Organization Name:</legend>
          <div class="w-1/2 flex-grow pr-4 relative">
            <input
              v-model="nonprofitName"
              type="text"
              name="nonprofit-name"
              placeholder="Nonprofit Name"
              class="input"
              :class="[displayErrors.nonprofitName !== '' ? 'border-red' : '']"
              @input="clearSignUpErrors('nonprofitName')"
            />
            <svg
              v-if="displayErrors.nonprofitName !== ''"
              class="absolute right-0 top-0 mr-7 mt-2.5"
              xmlns="http://www.w3.org/2000/svg"
              width="17.433"
              height="15.57"
              viewBox="0 0 17.433 15.57"
            >
              <path
                class="a"
                fill="red"
                d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
                transform="translate(-1.898 -4.978)"
              />
            </svg>
            <span
              v-if="displayErrors.nonprofitName !== ''"
              class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
              >{{ displayErrors.nonprofitName }}
            </span>
          </div>
          <div class="w-1/2 flex-grow pl-4 relative">
            <input
              v-model="profilePageName"
              type="text"
              name="profile-page-name"
              placeholder="Profile Page Name"
              class="input"
              :class="[displayErrors.profilePageName !== '' ? 'border-red' : '']"
              @input="clearSignUpErrors('profilePageName')"
            />
            <svg
              v-if="displayErrors.profilePageName !== ''"
              class="absolute right-0 top-0 mr-7 mt-2.5"
              xmlns="http://www.w3.org/2000/svg"
              width="17.433"
              height="15.57"
              viewBox="0 0 17.433 15.57"
            >
              <path
                class="a"
                fill="red"
                d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
                transform="translate(-1.898 -4.978)"
              />
            </svg>
            <span
              v-if="displayErrors.profilePageName !== ''"
              class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
              >{{ displayErrors.profilePageName }}
            </span>
          </div>
        </fieldset>
      </div>
      <div class="text-left">
        <fieldset class="mt-8 -mx-2">
          <label
            class="mb-3 text-left float-left text-gray"
            for="mission-statement"
            >Mission Statement:</label
          >
          <textarea
            id="mission-statement"
            v-model="missionStatement"
            class="input resize-none"
            name="mission-statement"
            rows="5"
            cols="33"
            :class="[displayErrors.missionStatement !== '' ? 'border-red' : '']"
            @input="clearSignUpErrors('missionStatement')"
          >
          </textarea>
          <span
            v-if="displayErrors.missionStatement !== ''"
            class=" mt-1.5 mr-7 text-red text-xxs capitalize text-right block"
            >{{ displayErrors.missionStatement }}
          </span>
        </fieldset>
      </div>
      <div class="text-left">
        <fieldset class="mt-8 flex -mx-2">
          <legend class="mb-3 text-gray">Primary Account Admin:</legend>
          <div class="w-1/2 flex-grow pr-4 relative">
            <input
              v-model="adminName"
              type="text"
              name="admin-name"
              placeholder="Name"
              class="input"
              :class="[displayErrors.adminName !== '' ? 'border-red' : '']"
              @input="clearSignUpErrors('adminName')"
            />
            <svg
              v-if="displayErrors.adminName !== ''"
              class="absolute right-0 top-0 mr-7 mt-2.5"
              xmlns="http://www.w3.org/2000/svg"
              width="17.433"
              height="15.57"
              viewBox="0 0 17.433 15.57"
            >
              <path
                class="a"
                fill="red"
                d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
                transform="translate(-1.898 -4.978)"
              />
            </svg>
            <span
              v-if="displayErrors.adminName !== ''"
              class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
              >{{ displayErrors.adminName }}
            </span>
          </div>
          <div class="w-1/2 flex-grow pl-4 relative">
            <input
              v-model="adminEmail"
              type="text"
              name="admin-email"
              placeholder="Email"
              class="input"
              :class="[displayErrors.adminEmail !== '' ? 'border-red' : '']"
              @input="clearSignUpErrors('adminEmail')"
            />
            <svg
              v-if="displayErrors.adminEmail !== ''"
              class="absolute right-0 top-0 mr-7 mt-2.5"
              xmlns="http://www.w3.org/2000/svg"
              width="17.433"
              height="15.57"
              viewBox="0 0 17.433 15.57"
            >
              <path
                class="a"
                fill="red"
                d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
                transform="translate(-1.898 -4.978)"
              />
            </svg>
            <span
              v-if="displayErrors.adminEmail !== ''"
              class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
              >{{ displayErrors.adminEmail }}
            </span>
          </div>
        </fieldset>
      </div>
      <div class="text-left">
        <fieldset class="mt-8 flex -mx-2">
          <legend class="mb-3 text-gray">Organization Info:</legend>
          <div class="w-1/2 flex-grow pr-4 relative">
            <input
              v-model="mainWebsite"
              type="text"
              name="main-website"
              placeholder="Main Website"
              class="input"
              :class="[displayErrors.mainWebsite !== '' ? 'border-red' : '']"
              @input="clearSignUpErrors('mainWebsite')"
            />
            <svg
              v-if="displayErrors.mainWebsite !== ''"
              class="absolute right-0 top-0 mr-7 mt-2.5"
              xmlns="http://www.w3.org/2000/svg"
              width="17.433"
              height="15.57"
              viewBox="0 0 17.433 15.57"
            >
              <path
                class="a"
                fill="red"
                d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
                transform="translate(-1.898 -4.978)"
              />
            </svg>
            <span
              v-if="displayErrors.mainWebsite !== ''"
              class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
              >{{ displayErrors.mainWebsite }}
            </span>
          </div>
          <div class="w-1/2 flex-grow pl-4 relative">
            <input
              v-model="volunteerWebsite"
              type="text"
              name="volunteer-website"
              placeholder="Volunteer Website"
              class="input"
              :class="[displayErrors.volunteerWebsite !== '' ? 'border-red' : '']"
              @input="clearSignUpErrors('volunteerWebsite')"
            />
            <svg
              v-if="displayErrors.volunteerWebsite !== ''"
              class="absolute right-0 top-0 mr-7 mt-2.5"
              xmlns="http://www.w3.org/2000/svg"
              width="17.433"
              height="15.57"
              viewBox="0 0 17.433 15.57"
            >
              <path
                class="a"
                fill="red"
                d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
                transform="translate(-1.898 -4.978)"
              />
            </svg>
            <span
              v-if="displayErrors.volunteerWebsite !== ''"
              class="right-0 absolute mt-1.5 mr-7 text-red text-xxs capitalize text-right block w-full"
              >{{ displayErrors.volunteerWebsite }}
            </span>
          </div>
        </fieldset>
      </div>
      <div class="text-left">
        <fieldset class="mt-8 -mx-2">
          <legend class="mb-3 text-gray"
            >Are you a nonprofit in good standing?</legend
          >
          <div class="flex items-center">
            <div class="flex items-center mr-4">
              <input
                id="yes-good-standing"
                v-model="isGoodStanding"
                type="radio"
                name="is-good-standing"
                class="checkbox mr-2"
                :value="1"
              />
              <label
                class="inline-flex items-center cursor-pointer"
                for="yes-good-standing"
              >
                Yes</label
              >
            </div>
            <div class="flex items-center mr-5">
              <input
                id="no-good-standing"
                v-model="isGoodStanding"
                type="radio"
                name="is-good-standing"
                class="checkbox mr-2"
                :value="0"
              />
              <label
                class="inline-flex items-center cursor-pointer"
                for="no-good-standing"
              >
                No</label
              >
            </div>
            <select
              v-show="isGoodStanding === 1"
              v-model="nonprofitClassification"
              class="select h-8 w-1/2"
              :class="[displayErrors.nonprofitClassification ? 'border-red' : '']"
              @change="clearSignUpErrors('nonprofitClassification')"
            >
              <option value="" disabled selected class="text-gray"
                >Nonprofit Classification</option
              >
              <option value="501(c)(3) - Charitable Organization"
                >501(c)(3) - Charitable Organization</option
              >
              <option value="501(c)(4) - Social Welfare Organization"
                >501(c)(4) - Social Welfare Organization</option
              >
              <option value="501(c)(5) - Labor/Agricultural Organization"
                >501(c)(5) - Labor/Agricultural Organization</option
              >
              <option value="501(c)(6) - Business League/Chamber of Commerce"
                >501(c)(6) - Business League/Chamber of Commerce</option
              >
              <option value="501(c)(7) - Social/Recreational Club"
                >501(c)(7) - Social/Recreational Club</option
              >
              <option value="501(c)(8) - Fraternal Beneficiary Society"
                >501(c)(8) - Fraternal Beneficiary Society</option
              >
              <option value="501(c)(10) - Domestic Fraternal Society"
                >501(c)(10) - Domestic Fraternal Society</option
              >
              <option value="501(c)(19) - Veterans Organization"
                >501(c)(19) - Veterans Organization</option
              >
              <option value="Educational Institution"
                >Educational Institution</option
              >
              <option value="Hospital/Healthcare Organization"
                >Hospital/Healthcare Organization</option
              >
              <option value="Scientific Research Organization"
                >Scientific Research Organization</option
              >
              <option value="Literary Organization">Literary Organization</option>
              <option value="Public Safety Testing Organization"
                >Public Safety Testing Organization</option
              >
              <option value="Amateur Sports Organization"
                >Amateur Sports Organization</option
              >
              <option value="Prevention of Cruelty to Children or Animals"
                >Prevention of Cruelty to Children or Animals</option
              >
              <option value="Community Foundation">Community Foundation</option>
              <option value="Private Foundation">Private Foundation</option>
              <option value="Public Charity">Public Charity</option>
            </select>
          </div>
          <span
            v-if="displayErrors.nonprofitClassification"
            class="mt-1.5 text-red text-xxs text-left block"
            >{{ displayErrors.nonprofitClassification }}
          </span>
        </fieldset>
      </div>
      <div class="text-left">
        <fieldset class="mt-8 flex -mx-2">
          <legend class="mb-3 text-gray"
            >Are you a religious organization?</legend
          >
          <div class="flex items-center mr-4">
            <input
              id="yes-is-religious"
              v-model="isReligious"
              type="radio"
              name="is-religious"
              class="checkbox mr-2"
              :value="1"
            />
            <label
              class="inline-flex items-center cursor-pointer"
              for="yes-is-religious"
            >
              Yes</label
            >
          </div>
          <div class="flex items-center mr-5">
            <input
              id="no-is-religious"
              v-model="isReligious"
              type="radio"
              name="is-religious"
              class="checkbox mr-2"
              :value="0"
            />
            <label
              class="inline-flex items-center cursor-pointer"
              for="no-is-religious"
            >
              No</label
            >
          </div>
        </fieldset>
      </div>
      <div class="text-left">
        <fieldset class="mt-8 flex-col -mx-2">
          <legend class="mb-3 text-gray text-left">
            <span class="mb-2"
              >Does Your Organization
              <a
                href="https://causes.benevity.org/sites/all/themes/benevity/CausesApp/docs/Discriminate_HyperlinkDoc.pdf"
                target="_blank"
                >Discriminate</a
              >
              against any person or group of people in its hiring and <br />
              employment practices, codes of conduct, programs, services or in
              any other aspect of its <br />
              operations or activities on the basis of that person or group of
              people's personal <br />
              characteristics or attributes?
            </span>
            <br />
            <span class="mb-3 text-gray text-left text-xs text-left">
              For clarity, Discrimination in this Question includes (but is not
              limited to) hiring and employment policies or practices that
              <br />
              Discriminate against a person or group of people on the basis of
              their sexual orientation or gender identity, even if such policies
              <br />
              and practices are permitted under applicable law.
            </span>
          </legend>

          <div class="flex">
            <div class="flex items-center mr-4">
              <input
                id="yes-is-discriminating"
                v-model="isDiscriminating"
                type="radio"
                name="is-discriminating"
                class="checkbox mr-2"
                :value="1"
              />
              <label
                class="inline-flex items-center cursor-pointer"
                for="yes-is-discriminating"
              >
                Yes</label
              >
            </div>
            <div class="flex items-center mr-5">
              <input
                id="no-is-discriminating"
                v-model="isDiscriminating"
                type="radio"
                name="is-discriminating"
                class="checkbox mr-2"
                :value="0"
              />
              <label
                class="inline-flex items-center cursor-pointer"
                for="no-is-discriminating"
              >
                No</label
              >
            </div>
          </div>
        </fieldset>
      </div>
      <div class="text-left">
        <fieldset class="mt-8 flex -mx-2">
          <legend class="mb-3 text-gray text-left"
            >Does Your Organization support or sponsor political campaigns or
            otherwise advocate for <br />(or against) political candidates?
          </legend>
          <div class="flex items-center mr-4">
            <input
              id="yes-is-political"
              v-model="isPolitical"
              type="radio"
              name="is-political"
              class="checkbox mr-2"
              :value="1"
            />
            <label
              class="inline-flex items-center cursor-pointer"
              for="yes-is-political"
            >
              Yes</label
            >
          </div>
          <div class="flex items-center mr-5">
            <input
              id="no-is-political"
              v-model="isPolitical"
              type="radio"
              name="is-political"
              class="checkbox mr-2"
              :value="0"
            />
            <label
              class="inline-flex items-center cursor-pointer"
              for="no-is-political"
            >
              No</label
            >
          </div>
        </fieldset>
      </div>
      <div class="text-left">
        <fieldset class="mt-8 flex -mx-2">
          <legend class="mb-3 text-gray text-left"
            >Your Organization will not lobby a federal, state or local
            government agency or government <br />
            official except where and only to the extent permitted under
            applicable laws.
          </legend>
          <div class="flex items-center mr-4">
            <input
              id="yes-is-federal"
              v-model="isFederal"
              type="radio"
              name="is-federal"
              class="checkbox mr-2"
              :value="1"
            />
            <label
              class="inline-flex items-center cursor-pointer"
              for="yes-is-federal"
            >
              Yes</label
            >
          </div>
          <div class="flex items-center mr-5">
            <input
              id="no-is-federal"
              v-model="isFederal"
              type="radio"
              name="is-federal"
              class="checkbox mr-2"
              :value="0"
            />
            <label
              class="inline-flex items-center cursor-pointer"
              for="vehicle1"
            >
              No</label
            >
          </div>
        </fieldset>
      </div>
      <div class="mt-8 mb-4">
        <button
          class="primary-button"
          :disabled="isTryingToSignupOrganization"
          @click="signupOrganization"
          ><span v-if="isTryingToSignupOrganization">Loading</span
          ><span v-if="!isTryingToSignupOrganization">Submit</span></button
        >
      </div>
    </div>
    <div v-if="successSignupOrganization" class="success-message mx-auto mt-5">
      <div class="mt-5 text-basel text-black">
        Thank you! Your organization has been sent for approval!<br />
        Please look out for an email regarding next steps.
      </div>
    </div>
  </div>
</template>
